import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

interface ScheduleItem {
  time: string;
  name: string;
  location: string;
  instructor?: string;
  description?: string;
  category: 'fitness' | 'arts' | 'social' | 'education' | 'music';
}

interface DaySchedule {
  day: string;
  items: ScheduleItem[];
}

const schedule: DaySchedule[] = [
  {
    day: 'Monday',
    items: [
      { time: '9:00 AM - 10:30 AM', name: 'Morning Tai Chi', location: 'Heritage Park', instructor: 'Master Chen', description: 'Start the week with gentle Tai Chi movements. All levels welcome.', category: 'fitness' },
      { time: '7:00 PM - 8:30 PM', name: 'Line Dancing', location: 'Community Center, Room A', instructor: 'Lisa Wang', description: 'Fun group line dancing with popular music. Great exercise and social activity.', category: 'fitness' },
    ],
  },
  {
    day: 'Tuesday',
    items: [
      { time: '9:00 AM - 10:00 AM', name: 'Tai Chi', location: 'Heritage Park', instructor: 'Master Chen', description: 'Continue your Tai Chi practice. Focus on form and breathing.', category: 'fitness' },
      { time: '10:30 AM - 12:00 PM', name: 'Chinese Painting', location: 'Art Studio', instructor: 'Prof. Zhang', description: 'Traditional Chinese ink painting techniques and practice.', category: 'arts' },
      { time: '2:00 PM - 4:00 PM', name: 'English Conversation', location: 'Library Meeting Room', description: 'Practice English in a friendly, supportive group setting.', category: 'education' },
    ],
  },
  {
    day: 'Wednesday',
    items: [
      { time: '10:00 AM - 11:30 AM', name: 'Chinese Painting', location: 'Art Studio', instructor: 'Prof. Zhang', description: 'Continued painting class with focus on landscape techniques.', category: 'arts' },
      { time: '2:00 PM - 4:00 PM', name: 'Mahjong Club', location: 'Senior Center', description: 'Casual mahjong games. Beginners welcome, we will teach you!', category: 'social' },
      { time: '7:00 PM - 9:00 PM', name: 'Choir Practice', location: 'Community Center', instructor: 'Director Liu', description: 'CAAOC community choir rehearsal. New members welcome.', category: 'music' },
    ],
  },
  {
    day: 'Thursday',
    items: [
      { time: '9:00 AM - 10:00 AM', name: 'Morning Exercise', location: 'Heritage Park', description: 'Group exercise and stretching in the park.', category: 'fitness' },
      { time: '2:00 PM - 4:00 PM', name: 'Mahjong Club', location: 'Senior Center', description: 'Afternoon mahjong sessions. All skill levels.', category: 'social' },
      { time: '7:00 PM - 8:30 PM', name: 'Ballroom Dancing', location: 'Community Center', instructor: 'David & May', description: 'Learn ballroom and social dance styles with partners.', category: 'fitness' },
    ],
  },
  {
    day: 'Friday',
    items: [
      { time: '10:00 AM - 12:00 PM', name: 'Calligraphy Workshop', location: 'Art Studio', instructor: 'Master Wei', description: 'Practice Chinese calligraphy. Materials provided for new students.', category: 'arts' },
      { time: '6:30 PM - 9:00 PM', name: 'Karaoke Night', location: 'Community Center', description: 'Sing your favorite songs in Chinese, English, or any language!', category: 'social' },
    ],
  },
  {
    day: 'Saturday',
    items: [
      { time: '8:00 AM - 9:30 AM', name: 'Tai Chi in the Park', location: 'Heritage Park', instructor: 'Master Chen', description: 'Weekend Tai Chi session. Beautiful outdoor practice.', category: 'fitness' },
      { time: '10:00 AM - 12:00 PM', name: 'Chinese Language School', location: 'Irvine Public Library', description: 'Chinese language classes for children and adults. Multiple levels available.', category: 'education' },
      { time: '1:00 PM - 3:00 PM', name: 'Youth Art Class', location: 'Art Studio', description: 'Creative art classes for kids ages 6-14.', category: 'arts' },
      { time: '3:00 PM - 5:00 PM', name: 'Badminton', location: 'Gymnasium', description: 'Open badminton play. Bring your own racket or borrow one.', category: 'fitness' },
    ],
  },
  {
    day: 'Sunday',
    items: [
      { time: '10:00 AM - 12:00 PM', name: 'Badminton', location: 'Gymnasium', description: 'Sunday morning badminton. Mixed doubles and singles.', category: 'fitness' },
      { time: '2:00 PM - 4:00 PM', name: 'Book Club', location: 'Library Meeting Room', description: 'Monthly book club discussion. Reading list shared in advance.', category: 'education' },
    ],
  },
];

const dayAbbreviations: Record<string, string> = {
  Monday: 'MON',
  Tuesday: 'TUE',
  Wednesday: 'WED',
  Thursday: 'THU',
  Friday: 'FRI',
  Saturday: 'SAT',
  Sunday: 'SUN',
};

const dayTranslationKeys: Record<string, string> = {
  Monday: 'schedule.monday',
  Tuesday: 'schedule.tuesday',
  Wednesday: 'schedule.wednesday',
  Thursday: 'schedule.thursday',
  Friday: 'schedule.friday',
  Saturday: 'schedule.saturday',
  Sunday: 'schedule.sunday',
};

const categoryKeys: Record<string, string> = {
  fitness: 'schedule.fitness',
  arts: 'schedule.arts',
  social: 'schedule.social',
  education: 'schedule.education',
  music: 'schedule.music',
};

export default function SchedulePage() {
  const { locale, t, prefix } = useLanguage();
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');

  const currentDayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  const displaySchedule = selectedDay
    ? schedule.filter((d) => d.day === selectedDay)
    : schedule;

  const categoryStyles: Record<string, { bg: string; color: string; label: string }> = {
    fitness: { bg: 'var(--green-bg)', color: 'var(--green-accent)', label: t('schedule.fitness') },
    arts: { bg: 'var(--orange-bg)', color: 'var(--orange-accent)', label: t('schedule.arts') },
    social: { bg: 'var(--pink-bg)', color: 'var(--pink-accent)', label: t('schedule.social') },
    education: { bg: 'var(--blue-bg)', color: 'var(--blue-accent)', label: t('schedule.education') },
    music: { bg: 'var(--yellow-bg)', color: 'var(--yellow-accent)', label: t('schedule.music') },
  };

  return (
    <div>
      {/* Hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
          color: '#fff',
          padding: '100px 0 60px',
        }}
      >
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '2.8rem', fontWeight: 900, marginBottom: 12 }}>{t('schedule.title')}</h1>
          <p style={{ fontSize: '1.15rem', opacity: 0.9, maxWidth: 600, margin: '0 auto' }}>
            {t('schedule.subtitle')}
          </p>
        </div>
      </section>

      <section style={{ padding: 'var(--section-padding)', background: 'var(--gray-100)' }}>
        <div className="container">
          {/* Day selector */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 24,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <button
              onClick={() => setSelectedDay(null)}
              style={{
                padding: '10px 20px',
                borderRadius: 24,
                border: 'none',
                background: selectedDay === null ? 'var(--gray-900)' : 'var(--white)',
                color: selectedDay === null ? '#fff' : 'var(--gray-600)',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              {t('schedule.allDays')}
            </button>
            {schedule.map((d) => (
              <button
                key={d.day}
                onClick={() => setSelectedDay(d.day)}
                style={{
                  padding: '10px 20px',
                  borderRadius: 24,
                  border: d.day === currentDayName ? '2px solid var(--red-primary)' : 'none',
                  background: selectedDay === d.day ? 'var(--red-primary)' : 'var(--white)',
                  color: selectedDay === d.day ? '#fff' : 'var(--gray-700)',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                {t(dayTranslationKeys[d.day])}
                {d.day === currentDayName && selectedDay !== d.day && (
                  <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--red-primary)' }}>{t('schedule.today')}</span>
                )}
              </button>
            ))}
          </div>

          {/* Category filter */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 32,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <button
              onClick={() => setFilterCategory('all')}
              style={{
                padding: '6px 16px',
                borderRadius: 20,
                border: '1px solid var(--gray-300)',
                background: filterCategory === 'all' ? 'var(--gray-200)' : 'transparent',
                color: 'var(--gray-700)',
                fontSize: '0.82rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {t('schedule.allCategories')}
            </button>
            {Object.entries(categoryStyles).map(([key, style]) => (
              <button
                key={key}
                onClick={() => setFilterCategory(key)}
                style={{
                  padding: '6px 16px',
                  borderRadius: 20,
                  border: filterCategory === key ? `2px solid ${style.color}` : '1px solid var(--gray-300)',
                  background: filterCategory === key ? style.bg : 'transparent',
                  color: filterCategory === key ? style.color : 'var(--gray-600)',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {style.label}
              </button>
            ))}
          </div>

          {/* Schedule display */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            {displaySchedule.map((day) => {
              const filteredItems =
                filterCategory === 'all'
                  ? day.items
                  : day.items.filter((item) => item.category === filterCategory);

              if (filteredItems.length === 0) return null;

              return (
                <div key={day.day}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      marginBottom: 16,
                    }}
                  >
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 'var(--radius-sm)',
                        background: day.day === currentDayName ? 'var(--red-primary)' : 'var(--gray-900)',
                        color: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '0.75rem',
                        letterSpacing: 1,
                        flexShrink: 0,
                      }}
                    >
                      {dayAbbreviations[day.day]}
                    </div>
                    <div>
                      <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--gray-900)' }}>
                        {t(dayTranslationKeys[day.day])}
                        {day.day === currentDayName && (
                          <span
                            style={{
                              marginLeft: 10,
                              fontSize: '0.75rem',
                              background: 'var(--red-bg)',
                              color: 'var(--red-primary)',
                              padding: '3px 10px',
                              borderRadius: 12,
                              fontWeight: 600,
                            }}
                          >
                            {t('schedule.today')}
                          </span>
                        )}
                      </h2>
                      <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                        {filteredItems.length} {filteredItems.length === 1 ? t('schedule.activity') : t('schedule.activities')}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingLeft: 72 }}>
                    {filteredItems.map((item, idx) => {
                      const cs = categoryStyles[item.category];
                      return (
                        <div
                          key={idx}
                          className="card"
                          style={{
                            display: 'flex',
                            gap: 20,
                            alignItems: 'flex-start',
                            padding: '20px 24px',
                            borderLeft: `4px solid ${cs.color}`,
                          }}
                        >
                          <div style={{ minWidth: 140 }}>
                            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--gray-800)' }}>
                              {item.time}
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--gray-900)' }}>
                                {item.name}
                              </h3>
                              <span
                                className="badge"
                                style={{ background: cs.bg, color: cs.color }}
                              >
                                {cs.label}
                              </span>
                            </div>
                            {item.description && (
                              <p style={{ fontSize: '0.88rem', color: 'var(--gray-600)', marginBottom: 6, lineHeight: 1.5 }}>
                                {item.description}
                              </p>
                            )}
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.83rem', color: 'var(--gray-500)' }}>
                              <span>&#128205; {item.location}</span>
                              {item.instructor && <span>&#128100; {item.instructor}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Note */}
          <div
            style={{
              marginTop: 48,
              padding: 24,
              background: 'var(--yellow-bg)',
              borderRadius: 'var(--radius-md)',
              borderLeft: '4px solid var(--yellow-accent)',
              fontSize: '0.9rem',
              color: 'var(--gray-700)',
            }}
          >
            <strong>{t('common.note')}:</strong> {t('schedule.note')}{' '}
            <Link to={prefix + '/contact'} style={{ color: 'var(--red-primary)', fontWeight: 600 }}>
              {t('schedule.contactUs')}
            </Link>
            .
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .card { flex-direction: column !important; }
        }
      `}</style>
    </div>
  );
}
